/* eslint-disable react/jsx-no-bind */
import { Box, Button, Card, CardContent, Divider, GridLegacy, TextField, Theme, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import Logo from 'src/components/Logo';
import useAuth from 'src/hooks/useAuth';
import { getPageTitle } from 'src/config/projectConfig';

const useStyles = makeStyles<Theme>((theme: Theme) =>
    createStyles({
        wrapper: {
            backgroundColor: '#E8EDF2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            border: 0,
            borderRadius: 0,
            [theme.breakpoints.down('md')]: {
                minHeight: 'auto',
                paddingTop: '2.5rem',
            },
        },
        firstSectionWrapper: {
            display: 'flex',
            justifyContent: 'center',
            background: 'url("/static/login_bg.png")',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            minHeight: '100vh',
            alignItems: 'center',
            flexDirection: 'column',
            width: '100%',
            [theme.breakpoints.down('md')]: {
                background: 'none',
                minHeight: '20vh',
            },
        },
        card: {
            backgroundColor: 'white',
            border: 0,
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            [theme.breakpoints.down('md')]: {
                minHeight: 'auto',
                paddingTop: '4.5rem',
            },
        },
        desktopLogo: {
            display: 'block',
            background: 'white',
            borderRadius: '50%',
            padding: '10px 10px',
            [theme.breakpoints.down('md')]: {
                display: 'none',
            },
        },
        mobileLogo: {
            display: 'none',
            [theme.breakpoints.down('md')]: {
                display: 'block',
            },
        },
        title: {
            color: '#fff',
            [theme.breakpoints.down('md')]: {
                color: 'black',
            },
        },
        subTitle: {
            color: '#fff',
            [theme.breakpoints.down('md')]: {
                display: 'none',
            },
        },
        leftHeroWrapper: {
            display: 'flex',
        },
        leftHeroImage: {
            maxWidth: '40%',
            background: 'white',
            objectFit: 'cover',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            [theme.breakpoints.down('md')]: {
                display: 'none',
            },
        },
        fullPageHero: {
            maxWidth: '100%',
        },
        imagesWrapper: {
            display: 'flex',
        },
        leftSideLogo: {
            background: 'white',
            display: 'flex',
            justifyContent: 'center',
            width: '50vw',
            flexDirection: 'column',
            alignItems: 'center',
            [theme.breakpoints.down('md')]: {
                width: '100vw',
            },
        },
        textWhite: {
            color: 'white',
        },
        logo: {
            width: 150,
            marginBottom: 24,
        },
    })
);

// Add decryption utility at the top
const decryptData = (encryptedData: string): string => {
    try {
        return decodeURIComponent(atob(encryptedData));
    } catch (error) {
        console.error('Decryption failed:', error);
        return '';
    }
};

const TwoFactorAuthentication = () => {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const { verifyTwoFactorCode } = useAuth();
    const qrCode = localStorage.getItem('qrCode');
    const isQrVerified = localStorage.getItem('isQrVerified') === 'true';
    const navigate = useNavigate();
    const classes = useStyles();

    useEffect(() => {
        const encryptedEmail = localStorage.getItem('_te');
        const encryptedPassword = localStorage.getItem('_tp');

        if (!encryptedEmail || !encryptedPassword) {
            setError('Session expired. Please login again.');
            navigate('/login');
            return;
        }

        const timeoutId = setTimeout(() => {
            localStorage.removeItem('_te');
            localStorage.removeItem('_tp');
            localStorage.removeItem('qrCode');
            localStorage.removeItem('isQrVerified');
            localStorage.removeItem('isOtpVerified');
            localStorage.removeItem('is2FAEnabled');
            navigate('/login');
            toast.error('Session expired. Please login again.');
        }, 120000); // 60 seconds

        // eslint-disable-next-line consistent-return
        return () => clearTimeout(timeoutId);
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const encryptedEmail = localStorage.getItem('_te');
            const encryptedPassword = localStorage.getItem('_tp');

            if (!encryptedEmail || !encryptedPassword) {
                setError('Session expired. Please login again.');
                navigate('/login');
                return;
            }

            const email = decryptData(encryptedEmail);
            const password = decryptData(encryptedPassword);

            if (!code || code.length !== 6) {
                setError('Please enter a valid 6-digit code');
                return;
            }

            await verifyTwoFactorCode({
                email,
                password,
                otp: code,
            });
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Invalid verification code');
        }
    };

    return (
        <>
            <Helmet>
                <title>{getPageTitle('Two-Factor Authentication')}</title>
            </Helmet>
            <Box className={classes.wrapper}>
                <GridLegacy
                    container
                    style={{ alignItems: 'center', justifyContent: 'center' }}>
                    <Card className={classes.card}>
                        <CardContent
                            style={{
                                padding: '70px 50px',
                                backgroundColor: 'transparent',
                                display: 'flex',
                                flexDirection: 'column',
                                width: '564px',
                            }}>
                            <Box
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: isQrVerified ? '32px' : '0px',
                                }}>
                                <GridLegacy
                                    container
                                    style={{
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 3,
                                    }}>
                                    <Logo width={80} />
                                    <Typography
                                        color="textPrimary"
                                        style={{ fontSize: '1.5em !important' }}
                                        variant="h3">
                                        {isQrVerified
                                            ? 'Two-Factor Authentication'
                                            : 'Setup Two-Factor Authentication (2FA)'}
                                    </Typography>
                                    {!isQrVerified && <Divider flexItem />}
                                </GridLegacy>
                            </Box>

                            <Box
                                style={{
                                    flexGrow: 1,
                                    marginTop: '8px',
                                }}>
                                {!isQrVerified && qrCode && (
                                    <Box
                                        style={{
                                            marginBottom: '16px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}>
                                        <ol>
                                            <li>
                                                Install one of the following applications on your
                                                mobile:
                                                <ul>
                                                    <li>Microsoft Authenticator</li>
                                                    <li>Google Authenticator</li>
                                                    <li>FreeOTP</li>
                                                </ul>
                                            </li>
                                            <li>Open the application and scan the QR Code:</li>
                                            <div>
                                                <img
                                                    src={qrCode}
                                                    alt="2FA QR Code"
                                                    style={{
                                                        width: 200,
                                                        height: 200,
                                                    }}
                                                />
                                            </div>
                                            <li>
                                                Enter the one-time code provided by the application and
                                                click verify.
                                            </li>
                                        </ol>
                                    </Box>
                                )}
                                <Box
                                    component="form"
                                    onSubmit={handleSubmit}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 2,
                                    }}>
                                    <TextField
                                        fullWidth
                                        label="Enter 6 digit verification code"
                                        variant="outlined"
                                        value={code}
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            if (/^\d*$/.test(value)) {
                                                // Only allow digits
                                                setCode(value);
                                            }
                                        }}
                                        error={!!error}
                                        helperText={error}
                                        inputProps={{
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*',
                                            maxLength: 6,
                                            style: { textAlign: 'center', letterSpacing: '0.5em' },
                                        }}
                                        style={{ marginBottom: '8px' }}
                                    />
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        disabled={code.length !== 6}>
                                        Verify
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        size="large"
                                        onClick={() => {
                                            localStorage.removeItem('is2FAEnabled');
                                            localStorage.removeItem('isQrVerified');
                                            localStorage.removeItem('isOtpVerified');
                                            localStorage.removeItem('_te');
                                            localStorage.removeItem('_tp');
                                            localStorage.removeItem('qrCode');
                                            navigate('/login');
                                        }}>
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </GridLegacy>
            </Box>
        </>
    );
};

export default TwoFactorAuthentication;
